import { useQuery } from "@tanstack/react-query";
import { workflowContainerVideosApi } from "./axios";
import {
  DataI,
  ServiceResponseI,
  PropsWorkflowContainerVideosI,
  WorkflowContainerVideosI,
  ObjPostI,
} from "../../interfaces";
import {
  ValidateCurrentToken,
  LogoutSystem,
  analyzeConnectionQuality,
  // logProcessingStep,
  // trackServerResponseDirect,
} from "../../helpers";
import axios, { AxiosError } from "axios";

const getWorkflowContainerVideos = async ({
  pagination,
  sorting,
  globalFilter,
  range,
  rangeVal,
}: PropsWorkflowContainerVideosI): Promise<
  ServiceResponseI<DataI<WorkflowContainerVideosI>>
> => {
  try {
    const validateToken = ValidateCurrentToken();

    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("page", (pagination!.pageIndex + 1).toString());
    params.append("limit", pagination!.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    rangeVal && params.append("range", JSON.stringify(range));

    //headers: { 'Authorization': validateToken
    const { data } = await workflowContainerVideosApi.get<
      ServiceResponseI<DataI<WorkflowContainerVideosI>>
    >("", { params, headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    console.log(err);
    const obj = (err as AxiosError).response;

    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }

    throw new Error((obj?.data as Error).message);
  }
};

export const getAllWorkflowContainerVideos = async (): Promise<
  ServiceResponseI<WorkflowContainerVideosI[]>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await workflowContainerVideosApi.get<
      ServiceResponseI<WorkflowContainerVideosI[]>
    >(`/all-no-pagination`, { headers: { Authorization: validateToken } });

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowContainerVideosPost = async <T>(
  getForms: T
): Promise<ServiceResponseI<ObjPostI>> => {
  const startTime = Date.now();

  try {
    // Validar el token
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    console.log("🎥 POST workflow-container-videos - Upload directo iniciado");

    // ✅ VERIFICAR CONEXIÓN ANTES DE PROCEDER
    const connectionInfo = analyzeConnectionQuality();
    
    if (!connectionInfo.canUpload) {
      // Si no hay buena conexión, retornar error inmediatamente
      const errorMessage = connectionInfo.quality === "sin-internet" 
        ? "No hay conexión a internet"
        : "Conexión insuficiente para videos. Se necesita WiFi o mejor señal.";
        
      return {
        data: {
          id: 0,
          alert: errorMessage,
          variant: "error",
        },
        res: {} as any,
        req: {} as any,
        isOffline: true,
      };
    }

    console.log(`📡 Conexión verificada: ${connectionInfo.message} (${connectionInfo.speedMbps.toFixed(1)} Mbps)`);

    // ✅ EXTRAER INFORMACIÓN DEL FORMDATA PARA LOGGING
    let videoMetadata: any = {};
    let videoFile: File | null = null;
    let videoSize = 0;
    let container = "";
    let type = "videoContenedorVacio";
    let containerType = "empty";

    if (getForms instanceof FormData) {
      const formEntries = Array.from((getForms as FormData).entries());
      
      formEntries.forEach(([key, value]) => {
        if (value instanceof File) {
          videoFile = value;
          videoSize = value.size;
          videoMetadata[`${key}_filename`] = value.name;
          videoMetadata[`${key}_size`] = value.size;
          videoMetadata[`${key}_type`] = value.type;
        } else if (typeof value === "string") {
          videoMetadata[key] = value;
          // Extraer valores importantes
          if (key === "container") container = value;
          if (key === "type") type = value as any;
          if (key === "containerType") containerType = value as any;
        }
      });

    }

    // ✅ VALIDACIONES BÁSICAS
    if (!videoFile) {
      throw new Error("No se encontró archivo de video en la solicitud");
    }

    if (!container || container.length < 3) {
      throw new Error("Número de contenedor inválido");
    }

    
    try {
      const response = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        // Crear FormData limpio (reutilizar el que viene)
        const formData = getForms as FormData;

        // Headers específicos para el tracking
        // xhr.setRequestHeader = ((originalSetRequestHeader) => {
        //   return function(name: string, value: string) {
        //     originalSetRequestHeader.call(this, name, value);
        //   };
        // })(xhr.setRequestHeader);

        // Manejar progreso (opcional - podrías exponerlo si necesitas)
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            console.log(`📈 Progreso upload: ${progress}%`);
          }
        };

        // Manejar respuesta exitosa
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const responseData = JSON.parse(xhr.responseText);
              console.log("✅ Upload exitoso:", responseData);
              resolve(responseData);
            } catch (parseError) {
              // Si no puede parsear JSON, asumir éxito básico
              console.log("✅ Upload exitoso (sin JSON válido)");
              resolve({
                id: Date.now(),
                alert: "Video subido correctamente",
                variant: "success"
              });
            }
          } else if (xhr.status === 401) {
            reject(new Error("TOKEN INVALIDO"));
          } else if (xhr.status === 409) {
            // Conflicto - probablemente video duplicado
            try {
              const conflictData = JSON.parse(xhr.responseText);
              resolve(conflictData);
            } catch {
              resolve({
                id: 0,
                alert: "Este contenedor ya tiene un video registrado",
                variant: "info",
                existed: true
              });
            }
          } else if (xhr.status >= 500) {
            reject(new Error("Error del servidor - intenta en unos minutos"));
          } else {
            reject(new Error(`Error del servidor: ${xhr.status} ${xhr.statusText}`));
          }
        };

        // Manejar errores de red
        xhr.onerror = () => {
          reject(new Error("Error de conexión - verifica tu internet"));
        };

        // Manejar timeout
        xhr.ontimeout = () => {
          reject(new Error("Tiempo de espera agotado - video muy grande para tu conexión"));
        };

        // ✅ TIMEOUT ADAPTATIVO BASADO EN CONEXIÓN Y TAMAÑO
        let timeoutMinutes = 5; // Base mínimo
        
        if (videoSize > 0) {
          const sizeMB = videoSize / (1024 * 1024);
          // Tiempo base por MB según calidad de conexión
          const timePerMB = connectionInfo.quality === "internet-bueno" ? 0.5 : 1;
          timeoutMinutes = Math.max(5, Math.ceil(sizeMB * timePerMB));
        }
        
        xhr.timeout = timeoutMinutes * 60 * 1000;
        console.log(`⏱️ Timeout configurado: ${timeoutMinutes} minutos`);

        // ✅ ENVIAR REQUEST
        const url = `${process.env.REACT_APP_BACKEND}api/workflow-container-videos`;
        xhr.open("POST", url);
        xhr.setRequestHeader("Authorization", validateToken); 
        
        // Headers adicionales para tracking/debugging
        xhr.setRequestHeader("X-Connection-Quality", connectionInfo.quality);
        xhr.setRequestHeader("X-Upload-Method", "direct");
        xhr.setRequestHeader("X-Video-Size", videoSize.toString());
        
        xhr.send(formData);
      });

      // ✅ FORMATEAR RESPUESTA EXITOSA
      const responseData = response as any;
      
      const finalResponse: ServiceResponseI<ObjPostI> = {
        data: {
          id: responseData.id || Date.now(),
          alert: responseData.alert || "Video subido correctamente",
          variant: responseData.variant || "success",
          // Campos adicionales para duplicados
          existed: responseData.existed || false,
          created: responseData.created !== false,
          originalDate: responseData.originalDate,
          status: responseData.status,
        },
        res: {} as any,
        req: {} as any,
        isOffline: false, // Siempre false para uploads directos exitosos
      };

      console.log(`✅ Upload completado en ${Date.now() - startTime}ms`);
      return finalResponse;

    } catch (uploadError: any) {
      console.error("❌ Error en upload directo:", uploadError.message);
      
      // ✅ MANEJAR ERRORES ESPECÍFICOS
      let errorAlert = "Error al subir el video";
      let errorVariant: "error" | "warning" = "error";
      
      if (uploadError.message.includes("TOKEN INVALIDO")) {
        // Error de autenticación - propagar
        throw uploadError;
      } else if (uploadError.message.includes("conexión")) {
        errorAlert = "Error de conexión. Verifica tu internet e intenta nuevamente.";
        errorVariant = "warning";
      } else if (uploadError.message.includes("Tiempo de espera")) {
        errorAlert = "El video es muy grande para tu conexión actual. Intenta con WiFi.";
        errorVariant = "warning";
      } else if (uploadError.message.includes("servidor")) {
        errorAlert = "Error temporal del servidor. Intenta en unos minutos.";
        errorVariant = "error";
      } else {
        errorAlert = uploadError.message || "Error desconocido al subir el video";
      }

      return {
        data: {
          id: 0,
          alert: errorAlert,
          variant: errorVariant,
        },
        res: {} as any,
        req: {} as any,
        isOffline: true, // Marcar como offline cuando falla
      };
    }

  } catch (err: any) {
    console.error("❌ Error general en workflowContainerVideosPost:", err);

    // ✅ MANEJAR ERRORES ESPECÍFICOS DE VALIDACIÓN
    if (err.message === "TOKEN INVALIDO") {
      console.error("🔐 Token inválido - redirigiendo al login");
      // Aquí podrías llamar LogoutSystem() si lo tienes disponible
      throw err;
    }

    // ✅ ERROR GENERAL
    return {
      data: {
        id: 0,
        alert: err.message || "Error al procesar la solicitud. Intenta nuevamente.",
        variant: "error",
      },
      res: {} as any,
      req: {} as any,
      isOffline: true,
    };
  }
};


export const workflowContainerVideosPut = async <T>(
  id: number,
  updateForms: T
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowContainerVideosApi.put<
      ServiceResponseI<string>
    >(`/${id}`, updateForms, { headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowContainerVideosDelete = async (
  id: number
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowContainerVideosApi.delete<
      ServiceResponseI<string>
    >(`/${id}`, { headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const useWorkflowContainerVideos = ({
  sorting,
  pagination,
  globalFilter,
  range,
  rangeVal,
}: PropsWorkflowContainerVideosI) => {
  const workflowContainerVideosQuery = useQuery(
    [
      "workflowContainerVideos",
      { pagination, sorting, globalFilter, range, rangeVal },
    ],
    () =>
      getWorkflowContainerVideos({
        pagination,
        sorting,
        globalFilter,
        range,
        rangeVal,
      }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  return {
    workflowContainerVideosQuery,
  };
};
